<template>
  <Layout>
    <div class="mc-report__expenses">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McListDownloader
              :pageOptions="downloadPageOptions"
              :fileFormatOptions="['excel']"
              @download="handleDownload"
            />
            <McButtonBack />
          </div>
        </template>
      </PageHeader>
      <McTable
        withCard
        searchable
        hasViewManager
        :fields="fields"
        :apiClient="apiClient"
        :hasCheckbox="false"
        :activeFilters="activeFilters"
      >
        <template #before-table>
          <div class="d-flex justify-content-between mb-3"> 
            <div class="mc-report-period">
              <div class="__label">{{ $t('report.object.reportPeriod') }}</div>
              <div class="__detail">{{ period }}</div>
            </div>
            <McTableFilter
              ref="expensesFilter"
              :title="$t('report.filters.reportParameter')"
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              :hasResetFilters="false"
              @submit="handleChangeActiveFilters"
            >
              <template #content>
                <InputTableFilter />
              </template>
            </McTableFilter>
          </div>
        </template>
        <template #cell()="{ field, value }">
          {{ formatValues(field.key, value) }}
        </template>
        <template #cell(date)="{ value }">
          <template v-if="value">
            <div>{{ formatIndexDate(value).date }}</div>
          </template>
          <template v-else>-</template>
        </template>
        <template #cell(reference_type)="{ value }">
          {{ value }}
        </template>
        <template #cell(attachment)="{ value }">
          <a v-if="value?.name" :href="value?.url" target="_blank">
            <div class="mc-text-primary">{{ value?.name }}</div>
          </a>
          <div v-else>-</div>
        </template>
      </McTable>
    </div>
  </Layout>
</template>

<script>
import omitBy from 'lodash/omitBy'

import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2.vue'
import McButtonBack from '@/components/McButtonBack'
import McTable from '@/components/McTable'
import McListDownloader from '@/components/McListDownloader'
import McTableFilter from '@/components/McTableFilter'
import * as Report from '@/services/rest-api/lib/report/report.js'
import { formatIndexDate } from '@/helpers/tzDate'

import fields from './lib/fields/fields'
import { formatValues } from './lib/helpers/formatValues'
import InputTableFilter from './components/InputTableFilter'
import parseFilterDataMappings from './lib/constants/parseFilterDataMappings'
import filterMappings from './lib/constants/filterMappings'
import moment from 'moment'
import { Helper } from '@/mixin/helper'

export default {
  page: {
    title: 'Report Delivery History',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    McTable,
    PageHeader,
    McButtonBack,
    McTableFilter,
    McListDownloader,
    InputTableFilter,
  },
  data() {
    return {
      params: {},
      metadata: null,
      activeFilters: {},
    }
  },
  computed: {
    title() {
      return this.$t('report.filters.expenses')
    },
    apiClient() {
      let getReportExpenses = Report.getReportExpenses(null, this.handleSetParams, this.handleSetMetadata)
      let finalFilters = this.activeFilters

      if (Object.keys(this.activeFilters).length === 0) {
        const expensesFilter = this.$store.state.report.expensesFilter

        const activeFilters = {}
        for (let [filterName, value] of Object.entries(expensesFilter)) {
           if (!this.parseFilterDataMappings[filterName]) continue
          // assign filters to activeFilters variable
          this.parseFilterDataMappings[filterName].forEach((filter, index) => {
            activeFilters[filter] = value[index]
          })
        }
        finalFilters = activeFilters
      }

      const queryParamFilters = []

      // mapping the activeFilters to be sent as query params
      Object.keys(finalFilters).forEach((key) => {
        let filterValue = finalFilters[key]

        if (key === 'transaction_date:gte') {
          filterValue = new Date(filterValue)
          filterValue.setHours(0, 0, 0)
          filterValue = filterValue.toISOString()
        } else if (key === 'transaction_date:lte') {
          filterValue = new Date(filterValue)
          filterValue.setHours(23, 59, 59)
          filterValue = filterValue.toISOString()
        }

        const filter = filterMappings[key]?.(key, filterValue) ?? undefined
        queryParamFilters.push(filter)
      })

      getReportExpenses = Report.getReportExpenses(
        queryParamFilters,
        this.handleSetParams,
        this.handleSetMetadata
      )
      return {
        get: getReportExpenses,
      }
    },
    fields() {
      return fields
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    period() {
      const expensesFilter = this.$store.state.report.expensesFilter
      return `${this.formatPeriod(expensesFilter.transaction_date[0])} - ${this.formatPeriod(expensesFilter.transaction_date[1])}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      const mcTableFilter = this.$refs.expensesFilter

      const expensesFilter = this.$store.state.report.expensesFilter
      for (let [filterName, value] of Object.entries(expensesFilter)) {
        mcTableFilter.updateFormValues({ key: filterName, value })
         if (!this.parseFilterDataMappings[filterName]) continue

        // assign filters to activeFilters variable
        this.parseFilterDataMappings[filterName].forEach((filter, index) => {
          this.activeFilters[filter] = value[index]
        })
      }
    })
  },
  methods: {
    formatIndexDate,
    formatValues,
    handleDownload(format, dataAmount) {
      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }
      
      const queries = Helper.methods.queryParamsToString(params)

      window.open(
        `${process.env.VUE_APP_API_FINANCIAL_RECORD_URL}/report/cost_list_report?${queries}`,
        '_self'
      )
    },
    handleSetParams(params) {
      this.params = params
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined) return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.generatePeriod()
    },
    generatePeriod() {
      const filters = {}
      for (const [key, val] of Object.entries(this.parseFilterDataMappings)) {
        let value = ''
        if (Array.isArray(val)) {
          value = []
          val.forEach(filter => {
            value.push(this.activeFilters[filter])
          })
        }
        if (typeof val === 'string') {
          value = this.activeFilters[val]
        }
        filters[key] = value
      }
      this.$store.commit('report/updateExpensesFilter', filters)
    },
    formatPeriod(date) {
      return moment(date, 'YYYY-MM-DD').format('DD MMM YYYY')
    }
  },
}
</script>
<style lang="scss" scoped>
.mc-report__expenses :deep(.mc-report-period) {
  .__label{
    font-size: 12px;
    color: $mep-color-text-300;
    margin-bottom: 5px;
  }
  .__detail{
    font-size: 13px;
    color: $mep-color-text-400;
  }
}
:deep(.b-table) {
  .mc-text-primary {
    color: $mep-primary-400;
  }
}
</style>
